import React, { useEffect, useMemo, useState } from 'react'
import type {
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementOptions,
  StripeExpressCheckoutElementShippingAddressChangeEvent,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementReadyEvent,
  Stripe,
  StripeError,
} from '@stripe/stripe-js'

import { useShopStores } from 'shop/hooks/use-store'

import { PAYMENT_FORMS, PAYMENT_PROVIDERS } from 'constants/paymentSettingShared.constants'

import { notify } from 'libs/common/notify'

import { AblefyStripeExpressElementProvider } from 'shop/components/ablefy-stripe-express-element/AblefyStripeExpressElementProvider'
import useStripePromise from 'shared/hooks/useStripePromise'
import { getStripeElementsMode } from '@elo-kit/utils/stripe.utils'
import {
  confirmExpressCheckoutOrder,
  getExpressElementsBaseOptions,
  getExpressElementsUpdateOptions,
} from './expressCheckout.utils'
import { useExpressStripeElements } from './hooks/useExpressStripeElements'

interface Props {
  googleProvider: string
  googlePubKey: string
  payOptions: {
    amount: number
    currency: string
  }
  withSetupIntent?: boolean
}

export const GooglePayOneClick: React.FC<Props> = (props) => {
  const { googleProvider, googlePubKey, payOptions, withSetupIntent = false } = props
  const [stripeClient, setStripeClient] = useState<Stripe>(null)
  const [isReady, setIsReady] = useState<boolean>(false)
  const { elements, elementsMatchesMode, setStripeElements } = useExpressStripeElements(withSetupIntent)

  const { initStripePromise, getPromiseByProvider } = useStripePromise()
  const { paymentStore, sellerStore } = useShopStores()

  useEffect(() => {
    if (googlePubKey) {
      initStripePromise(googlePubKey, googleProvider)
    }
  }, [googlePubKey])

  useEffect(() => {
    setIsReady(false)
  }, [withSetupIntent])

  useEffect(() => {
    if (elements && elementsMatchesMode) {
      elements.update(getExpressElementsUpdateOptions(withSetupIntent, payOptions))
    }
  }, [payOptions.amount, payOptions.currency, elements, withSetupIntent])

  const elopageConnectAccountId = sellerStore.item.elopageConnectAccountId

  /* Elements' internal effect re-attaches a resolver to `stripePromise` on every render where `options` is a
     new object reference — passing a fresh object each render can create duplicate internal Elements instances */
  const stripeElementOptions = useMemo(() => {
    const providerOptions =
      googleProvider === PAYMENT_PROVIDERS.elopageConnect ? { onBehalfOf: elopageConnectAccountId } : {}

    return {
      ...getExpressElementsBaseOptions(withSetupIntent, payOptions),
      ...providerOptions,
    }
  }, [googleProvider, elopageConnectAccountId, withSetupIntent, payOptions.amount, payOptions.currency])

  const getAllowedShippingCountries = () => {
    const expressCountries = paymentStore.getAllowedExpressSellingCountries()
    return expressCountries.length ? { allowedShippingCountries: expressCountries } : {}
  }

  const expressElementOptions: StripeExpressCheckoutElementOptions = {
    paymentMethods: {
      googlePay: 'always',
      applePay: 'never',
      link: 'never',
      klarna: 'never',
      amazonPay: 'never',
      paypal: 'never',
    },
    shippingAddressRequired: true,
    shippingRates: [
      {
        id: 'free-shipping',
        displayName: I18n.t('react.shop.payment.free_shipping'),
        amount: 0,
      },
    ],
    emailRequired: true,
    phoneNumberRequired: true,
    ...getAllowedShippingCountries(),
  }

  const onReady = ({ availablePaymentMethods }: StripeExpressCheckoutElementReadyEvent) => {
    paymentStore.setGoogleAvailability(availablePaymentMethods || {})
    setIsReady(true)
  }

  const onClick = (event: StripeExpressCheckoutElementClickEvent) => {
    paymentStore.handleTrackingExpressClick(PAYMENT_FORMS.googlePay)
    event.resolve()
  }

  const onLoadError = ({ error }: { error: StripeError }) => {
    notify('error', error.message)
  }

  const onConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    /* After a mode flip the previous-mode Elements instance may still be registered — it cannot confirm the new intent */
    if (!elementsMatchesMode) {
      notify('error', I18n.t('react.shared.notify.error'))
      paymentStore.hideLoading()

      return
    }

    const resp = await paymentStore.handleOneClickPayment(
      event.shippingAddress,
      event.billingDetails,
      PAYMENT_FORMS.googlePay
    )

    await confirmExpressCheckoutOrder(stripeClient, elements, resp, (error: StripeError) => {
      notify('error', error.message)
      paymentStore.hideLoading()
    })
  }

  const onShippingAddressChange = async (event: StripeExpressCheckoutElementShippingAddressChangeEvent) => {
    if (!elementsMatchesMode) {
      event.reject()
      return
    }

    await paymentStore.updateExpressPrice(event, elements, withSetupIntent)
  }

  const stripePromise = getPromiseByProvider(googleProvider)

  if (!stripePromise) return null

  return (
    <div data-hidden-element={!isReady || !elementsMatchesMode}>
      <AblefyStripeExpressElementProvider
        key={getStripeElementsMode(withSetupIntent)}
        stripePromise={stripePromise}
        dataTestId='google-pay-one-click-test-id'
        options={stripeElementOptions}
        elementProps={{
          options: expressElementOptions,
          setStripeElements,
          setStripeClient,
          onReady,
          onClick,
          onConfirm,
          onLoadError,
          onShippingAddressChange,
        }}
      />
    </div>
  )
}
